import { useQuery } from "@tanstack/react-query";
import { apiRequest, type ApiError } from "@/shared/api";

export type EducationAuthResponse = {
  loginUrl: string;
  token?: string | null;
};

const educationAuthRequest = {
  url: "/integrations/edu/auth",
  method: "get" as const,
};

const asNonEmptyString = (value: unknown) =>
  typeof value === "string" && value.trim().length > 0 ? value.trim() : null;

export const normalizeEducationAuthResponse = (
  payload: unknown
): EducationAuthResponse => {
  if (payload && typeof payload === "object") {
    const source = payload as Record<string, unknown>;

    return {
      loginUrl:
        asNonEmptyString(source.loginUrl) ??
        asNonEmptyString(source.url) ??
        asNonEmptyString(source.redirectUrl) ??
        "",
      token:
        asNonEmptyString(source.token) ??
        asNonEmptyString(source.authToken) ??
        null,
    };
  }

  return {
    loginUrl: "",
    token: null,
  };
};

export const buildEducationPortalAuthUrl = (
  data?: EducationAuthResponse | null
) => {
  if (!data?.loginUrl) {
    return null;
  }

  if (!data.token) {
    return data.loginUrl;
  }

  const separator = data.loginUrl.includes("?") ? "&" : "?";
  return `${data.loginUrl}${separator}token=${encodeURIComponent(data.token)}`;
};

export const openEducationPortalWindow = (
  authUrl: string,
  targetUrl: string,
  delayMs = 2000
) => {
  const popup = window.open(authUrl, "_blank");

  window.setTimeout(() => {
    if (popup && !popup.closed) {
      popup.location.href = targetUrl;
    }
  }, delayMs);

  return popup;
};

export const getEducationAuth = async (): Promise<EducationAuthResponse> => {
  const response = await apiRequest<unknown>(educationAuthRequest);
  const normalized = normalizeEducationAuthResponse(response);

  if (!normalized.loginUrl) {
    throw new Error("Unable to generate authentication URL");
  }

  return normalized;
};

export const useEducationAuth = () =>
  useQuery<EducationAuthResponse, ApiError>({
    queryKey: ["learning", "education-auth"],
    queryFn: getEducationAuth,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
